import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/lib/auth';
import { useDismissedUIElements } from '@/hooks/useDismissedUIElements';

interface OnboardingTourContextType {
  shouldShowTour: boolean;
  isLoading: boolean;
  completeTour: () => void;
  startTour: () => void;
  tourKey: number;
  userRole: string | undefined;
}

const OnboardingTourContext = createContext<OnboardingTourContextType | undefined>(undefined);

export function OnboardingTourProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [shouldShow, setShouldShow] = useState(false);
  const [tourKey, setTourKey] = useState(0);
  
  // Determine the dismissal key based on user role
  const dismissKey = user?.role === 'vendor' 
    ? 'vendor_onboarding_tour' 
    : 'client_onboarding_tour';
  
  const { 
    isDismissed, 
    isLoading: isDismissedLoading, 
    dismiss 
  } = useDismissedUIElements(dismissKey);
  
  // Check if user is a client or vendor
  const isClient = user?.role === 'client';
  const isVendor = user?.role === 'vendor';
  
  useEffect(() => {
    // Show tour if: (client or vendor role) + not dismissed
    if ((isClient || isVendor) && !isDismissed && !isDismissedLoading) {
      // Small delay to let dashboard load
      const timer = setTimeout(() => setShouldShow(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [isClient, isVendor, isDismissed, isDismissedLoading]);
  
  const completeTour = () => {
    dismiss();
    setShouldShow(false);
  };
  
  const startTour = () => {
    setTourKey(prev => prev + 1);
    setShouldShow(true);
  };
  
  const value = {
    shouldShowTour: shouldShow,
    isLoading: isDismissedLoading,
    completeTour,
    startTour,
    tourKey,
    userRole: user?.role
  };

  return (
    <OnboardingTourContext.Provider value={value}>
      {children}
    </OnboardingTourContext.Provider>
  );
}

export function useOnboardingTourContext() {
  const context = useContext(OnboardingTourContext);
  if (context === undefined) {
    throw new Error('useOnboardingTourContext must be used within OnboardingTourProvider');
  }
  return context;
}
